/**
 * Value Object Money - représente un montant monétaire
 *
 * Règles métier:
 * - Le montant doit être >= 0
 * - La devise est toujours XOF (FCFA)
 * - Immutable
 */

import { CURRENCY } from '../../shared/constants/denominations';

export class Money {
  private constructor(
    private readonly _value: number,
    private readonly _currency: typeof CURRENCY
  ) {
    if (_value < 0) {
      throw new Error('Money value cannot be negative');
    }
    Object.freeze(this);
  }

  /**
   * Crée une instance Money
   */
  static create(value: number, currency: typeof CURRENCY = 'XOF'): Money {
    return new Money(value, currency);
  }

  /**
   * Crée une Money avec valeur zéro
   */
  static zero(): Money {
    return new Money(0, 'XOF');
  }

  get value(): number {
    return this._value;
  }

  get currency(): typeof CURRENCY {
    return this._currency;
  }

  /**
   * Compare deux Money
   */
  equals(other: Money): boolean {
    return this._value === other._value && this._currency === other._currency;
  }

  /**
   * Vérifie si le montant est zéro
   */
  isZero(): boolean {
    return this._value === 0;
  }

  /**
   * Vérifie si le montant est positif
   */
  isPositive(): boolean {
    return this._value > 0;
  }

  /**
   * Additionne deux Money
   */
  add(other: Money): Money {
    if (this._currency !== other._currency) {
      throw new Error('Cannot add money with different currencies');
    }
    return new Money(this._value + other._value, this._currency);
  }

  /**
   * Soustrait deux Money
   */
  subtract(other: Money): Money {
    if (this._currency !== other._currency) {
      throw new Error('Cannot subtract money with different currencies');
    }
    const result = this._value - other._value;
    if (result < 0) {
      throw new Error('Cannot create negative money');
    }
    return new Money(result, this._currency);
  }

  /**
   * Multiplie le montant par un facteur
   */
  multiply(factor: number): Money {
    if (factor < 0) {
      throw new Error('Cannot multiply by negative factor');
    }
    return new Money(this._value * factor, this._currency);
  }

  /**
   * Compare si ce montant est supérieur à un autre
   */
  isGreaterThan(other: Money): boolean {
    if (this._currency !== other._currency) {
      throw new Error('Cannot compare money with different currencies');
    }
    return this._value > other._value;
  }

  /**
   * Compare si ce montant est supérieur ou égal à un autre
   */
  isGreaterThanOrEqual(other: Money): boolean {
    if (this._currency !== other._currency) {
      throw new Error('Cannot compare money with different currencies');
    }
    return this._value >= other._value;
  }

  /**
   * Compare si ce montant est inférieur à un autre
   */
  isLessThan(other: Money): boolean {
    if (this._currency !== other._currency) {
      throw new Error('Cannot compare money with different currencies');
    }
    return this._value < other._value;
  }

  /**
   * Compare si ce montant est inférieur ou égal à un autre
   */
  isLessThanOrEqual(other: Money): boolean {
    if (this._currency !== other._currency) {
      throw new Error('Cannot compare money with different currencies');
    }
    return this._value <= other._value;
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): { value: number; currency: string } {
    return {
      value: this._value,
      currency: this._currency,
    };
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(json: { value: number; currency: string }): Money {
    if (json.currency !== 'XOF') {
      throw new Error('Only XOF currency is supported');
    }
    return new Money(json.value, json.currency as typeof CURRENCY);
  }

  /**
   * Représentation textuelle
   */
  toString(): string {
    return `${this._value} ${this._currency}`;
  }
}
